import * as React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale, useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { Agent, AdminUserRow, AdminWorkspaceRow, Connection, Schedule } from "@/lib/types"
import { formatCostUsd, formatDateTime } from "@/lib/utils"

export function AdminUserDetailPage() {
  const { userId } = useParams()
  const navigate = useNavigate()
  const { locale } = useLocale()
  const t = useT()
  const [row, setRow] = React.useState<AdminUserRow | null>(null)
  const [agents, setAgents] = React.useState<Agent[]>([])
  const [connections, setConnections] = React.useState<Connection[]>([])
  const [schedules, setSchedules] = React.useState<Schedule[]>([])
  const [workspaces, setWorkspaces] = React.useState<AdminWorkspaceRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [planSel, setPlanSel] = React.useState<Record<string, string>>({})
  const [daysSel, setDaysSel] = React.useState<Record<string, string>>({})
  const [grantBusy, setGrantBusy] = React.useState<string | null>(null)
  const [amountSel, setAmountSel] = React.useState<Record<string, string>>({})
  const [topUpBusy, setTopUpBusy] = React.useState<string | null>(null)

  const loadWorkspaces = React.useCallback(
    (id: string) =>
      api
        .adminUserWorkspaces(id)
        .then(setWorkspaces)
        .catch((error) =>
          toast.error(error instanceof ApiError ? error.message : t("admin.grant.loadFailed")),
        ),
    [t],
  )

  React.useEffect(() => {
    if (!userId) return
    setLoading(true)
    Promise.all([
      api.adminUsers(200, 0),
      api.adminUserAgents(userId),
      api.adminUserConnections(userId),
      api.adminUserSchedules(userId),
      api.adminUserWorkspaces(userId),
    ])
      .then(([page, agentRows, connectionRows, scheduleRows, workspaceRows]) => {
        setRow(page.items.find((item) => item.id === userId) ?? null)
        setAgents(agentRows)
        setConnections(connectionRows)
        setSchedules(scheduleRows)
        setWorkspaces(workspaceRows)
      })
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load this user."),
      )
      .finally(() => setLoading(false))
  }, [userId])

  async function grant(workspace: AdminWorkspaceRow) {
    if (!userId) return
    const planKey = planSel[workspace.id] ?? "pro"
    // Backend bounds days to 1..3650; mirror that here so a typo fails before the call.
    const days = Number.parseInt(daysSel[workspace.id] ?? "30", 10)
    if (!Number.isFinite(days) || days < 1 || days > 3650) {
      toast.error(t("admin.grant.daysInvalid"))
      return
    }
    setGrantBusy(workspace.id)
    try {
      await api.adminGrantPlan(workspace.id, planKey, days)
      toast.success(t("admin.grant.granted", { plan: planKey, workspace: workspace.name, days }))
      await loadWorkspaces(userId)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("admin.grant.grantFailed"))
    } finally {
      setGrantBusy(null)
    }
  }

  async function topUp(workspace: AdminWorkspaceRow) {
    if (!userId) return
    // Admin enters dollars; the backend stores micro-USD and bounds 1..10_000_000_000.
    const dollars = Number.parseFloat(amountSel[workspace.id] ?? "")
    const micro = Math.round(dollars * 1_000_000)
    if (!Number.isFinite(dollars) || micro < 10_000 || micro > 10_000_000_000) {
      toast.error(t("admin.grant.amountInvalid"))
      return
    }
    setTopUpBusy(workspace.id)
    try {
      await api.adminTopUp(workspace.id, micro)
      toast.success(
        t("admin.grant.toppedUp", { amount: formatCostUsd(dollars), workspace: workspace.name }),
      )
      setAmountSel((s) => ({ ...s, [workspace.id]: "" }))
      await loadWorkspaces(userId)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("admin.grant.topUpFailed"))
    } finally {
      setTopUpBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        title={row?.email ?? "User"}
        description="Configure this account's agents and tool servers; view its schedules."
        action={
          <Button variant="outline" onClick={() => navigate("/admin/users")}>
            Back to users
          </Button>
        }
      />
      <div className="space-y-6 p-6">
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <>
            <div>
              <h2 className="mb-3 text-sm font-semibold">
                {t("admin.grant.workspacesTitle")} ({workspaces.length})
              </h2>
              <p className="text-muted-foreground mb-3 text-sm">
                {t("admin.grant.workspacesDescription")}
              </p>
              {workspaces.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t("admin.grant.none")}</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">
                          {t("admin.grant.columnWorkspace")}
                        </th>
                        <th className="px-4 py-2 text-left font-medium">
                          {t("admin.grant.columnRole")}
                        </th>
                        <th className="px-4 py-2 text-left font-medium">
                          {t("admin.grant.columnPlan")}
                        </th>
                        <th className="px-4 py-2 text-left font-medium">
                          {t("admin.grant.columnRenews")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium">
                          {t("admin.grant.columnBalance")}
                        </th>
                        <th className="px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {workspaces.map((workspace) => (
                        <tr key={workspace.id} className="border-t">
                          <td className="px-4 py-2 font-medium">{workspace.name}</td>
                          <td className="px-4 py-2">
                            <Badge variant="outline">{workspace.role}</Badge>
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant="outline">{workspace.plan_key}</Badge>
                          </td>
                          <td className="text-muted-foreground px-4 py-2">
                            {formatDateTime(workspace.period_end, locale)}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {formatCostUsd(workspace.balance_micro_usd / 1_000_000)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="flex flex-wrap items-center justify-end gap-2">
                              <select
                                className="rounded border bg-background px-1 py-0.5 text-xs"
                                value={planSel[workspace.id] ?? "pro"}
                                onChange={(e) =>
                                  setPlanSel((s) => ({ ...s, [workspace.id]: e.target.value }))
                                }
                              >
                                <option value="free">free</option>
                                <option value="pro">pro</option>
                                <option value="team">team</option>
                              </select>
                              <input
                                type="number"
                                min={1}
                                max={3650}
                                className="w-16 rounded border bg-background px-1 py-0.5 text-xs"
                                value={daysSel[workspace.id] ?? "30"}
                                onChange={(e) =>
                                  setDaysSel((s) => ({ ...s, [workspace.id]: e.target.value }))
                                }
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={grantBusy === workspace.id}
                                onClick={() => grant(workspace)}
                              >
                                {t("admin.grant.button")}
                              </Button>
                              <input
                                type="number"
                                min={0.01}
                                max={10000}
                                step={0.01}
                                placeholder="$"
                                className="w-20 rounded border bg-background px-1 py-0.5 text-xs"
                                value={amountSel[workspace.id] ?? ""}
                                onChange={(e) =>
                                  setAmountSel((s) => ({ ...s, [workspace.id]: e.target.value }))
                                }
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={topUpBusy === workspace.id}
                                onClick={() => topUp(workspace)}
                              >
                                {t("admin.grant.topUpButton")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  navigate(`/admin/workspaces/${workspace.id}/members`)
                                }
                              >
                                Members
                              </Button>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold">Agents ({agents.length})</h2>
                <Button
                  size="sm"
                  onClick={() => navigate(`/admin/users/${userId}/agents/new`)}
                >
                  New agent
                </Button>
              </div>
              {agents.length === 0 ? (
                <p className="text-muted-foreground text-sm">No agents.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {agents.map((agent) => (
                    <Card
                      key={agent.id}
                      role="button"
                      tabIndex={0}
                      className="hover:border-primary/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/users/${userId}/agents/${agent.id}`)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault()
                          navigate(`/admin/users/${userId}/agents/${agent.id}`)
                        }
                      }}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">{agent.name}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-muted-foreground space-y-1 text-xs">
                        <div>
                          {agent.provider_key} · {agent.model}
                        </div>
                        <div>
                          {agent.tool_count} tool{agent.tool_count === 1 ? "" : "s"} bound
                        </div>
                        <Badge variant={agent.is_active ? "success" : "outline"}>
                          {agent.is_active ? "active" : "inactive"}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold">
                  MCP connections ({connections.length})
                </h2>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/admin/users/${userId}/connections`)}
                >
                  Manage connections
                </Button>
              </div>
              {connections.length === 0 ? (
                <p className="text-muted-foreground text-sm">No connections.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Name</th>
                        <th className="px-4 py-2 text-left font-medium">URL</th>
                        <th className="px-4 py-2 text-left font-medium">Status</th>
                        <th className="px-4 py-2 text-left font-medium">Tools</th>
                        <th className="px-4 py-2 text-left font-medium">Auth</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections.map((connection) => (
                        <tr key={connection.id} className="border-t">
                          <td className="px-4 py-2">{connection.name}</td>
                          <td className="text-muted-foreground px-4 py-2">{connection.url}</td>
                          <td className="px-4 py-2">
                            <Badge variant={connection.status === "ok" ? "success" : "outline"}>
                              {connection.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-2">{connection.tool_count}</td>
                          <td className="px-4 py-2">
                            {connection.auth.has_secret
                              ? `set (${connection.auth.hint ?? "…"})`
                              : "none"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <h2 className="mb-3 text-sm font-semibold">Schedules ({schedules.length})</h2>
              {schedules.length === 0 ? (
                <p className="text-muted-foreground text-sm">No schedules.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Name</th>
                        <th className="px-4 py-2 text-left font-medium">Agent</th>
                        <th className="px-4 py-2 text-left font-medium">Kind</th>
                        <th className="px-4 py-2 text-left font-medium">Next run</th>
                        <th className="px-4 py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map((schedule) => (
                        <tr key={schedule.id} className="border-t">
                          <td className="px-4 py-2">{schedule.name}</td>
                          <td className="px-4 py-2">{schedule.agent_name ?? "—"}</td>
                          <td className="px-4 py-2">
                            <Badge variant="outline">{schedule.kind}</Badge>
                          </td>
                          <td className="text-muted-foreground px-4 py-2">
                            {formatDateTime(schedule.next_run_at, locale)}
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant={schedule.is_active ? "success" : "outline"}>
                              {schedule.is_active ? "active" : "disabled"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}
